import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { applyPosterFallback, getEventPoster } from "@/lib/eventDisplay";

export default function FeaturedAdsCarousel({ language = "All", campaign = "" }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("language", language);
    if (campaign) params.set("campaign", campaign);
    api.get(`/ads/featured?${params.toString()}`)
      .then((r) => setAds(r.data || []))
      .catch(() => setAds([]));
  }, [language, campaign]);

  const heroAds = ads.filter((a) => a.format === "hero" && a.event);
  const cardAds = ads.filter((a) => a.format === "card" && a.event);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (heroAds.length > 1) {
      timerRef.current = setInterval(() => setIdx((i) => (i + 1) % heroAds.length), 4000);
    }
    return () => clearInterval(timerRef.current);
  }, [heroAds.length]);

  useEffect(() => {
    heroAds.forEach((a) => api.post(`/ads/${a.ad_id}/impression`).catch(() => {}));
    cardAds.forEach((a) => api.post(`/ads/${a.ad_id}/impression`).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads.length]);

  if (!heroAds.length && !cardAds.length) return null;

  const current = heroAds[idx];

  return (
    <section className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="featured-ads-section">
      {current && (
        <div className="md:col-span-8 md:row-span-2 relative overflow-hidden rounded-3xl border border-border bg-card group">
          <img src={getEventPoster(current.event)} alt={current.event.title} loading="eager" fetchPriority="high" decoding="async" className="w-full h-72 md:h-[26rem] object-cover" onError={(e) => applyPosterFallback(e, current.event)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold">
            <Sparkles className="h-3.5 w-3.5" /> FEATURED
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
            <p className="text-xs uppercase tracking-[0.25em] text-white/70 font-bold">{current.event.category} · {current.event.city}</p>
            <h2 className="font-display text-3xl md:text-5xl font-black mt-2 max-w-xl drop-shadow-lg">{current.event.title}</h2>
            <button
              onClick={() => { api.post(`/ads/${current.ad_id}/click`).catch(() => {}); navigate(`/event/${current.event.event_id}`); }}
              data-testid="featured-ad-cta"
              className="mt-4 inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:scale-[1.02] transition-transform"
            >
              Book now →
            </button>
          </div>
          {heroAds.length > 1 && (
            <div className="absolute right-4 top-4 flex gap-2">
              <button onClick={() => setIdx((i) => (i - 1 + heroAds.length) % heroAds.length)} className="h-8 w-8 rounded-full bg-black/50 text-white grid place-items-center"><ChevronLeft className="h-4 w-4"/></button>
              <button onClick={() => setIdx((i) => (i + 1) % heroAds.length)} className="h-8 w-8 rounded-full bg-black/50 text-white grid place-items-center"><ChevronRight className="h-4 w-4"/></button>
            </div>
          )}
        </div>
      )}

      {cardAds.slice(0, 4).map((a) => (
        <button
          key={a.ad_id}
          onClick={() => { api.post(`/ads/${a.ad_id}/click`).catch(() => {}); navigate(`/event/${a.event.event_id}`); }}
          data-testid={`also-featured-${a.ad_id}`}
          className="md:col-span-4 relative h-44 md:h-[12.5rem] rounded-2xl overflow-hidden border border-border text-left group"
        >
          <img src={getEventPoster(a.event)} alt={a.event.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" onError={(e) => applyPosterFallback(e, a.event)} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">Also Featured</p>
            <p className="font-display text-lg font-bold mt-1 line-clamp-2">{a.event.title}</p>
          </div>
        </button>
      ))}
    </section>
  );
}
